// Device data controls: see what this device stores and delete it.
// Used on /privacy. No accounts — everything is keyed by the anonymous device ID.

import React from "react";
import { Loader2, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "./ui/sonner";
import { api } from "../lib/api";

export default function DataControls() {
  const [summary, setSummary] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [confirming, setConfirming] = React.useState(false);
  const [done, setDone] = React.useState(null);

  const loadSummary = async () => {
    setLoading(true);
    try {
      const res = await api.get("/me/data");
      setSummary(res.data);
    } catch {
      toast.error("Could not load your data summary.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      const res = await api.delete("/me/data");
      setDone(res.data);
      setSummary(null);
      try {
        localStorage.clear(); // device ID, driver profile, saved car, local flags
      } catch {
        /* ignore */
      }
      toast.success("Your SpotDrop data was deleted.");
    } catch {
      toast.error("Could not delete your data. Please try again.");
    } finally {
      setLoading(false);
      setConfirming(false);
    }
  };

  return (
    <div data-testid="privacy-data-controls">
      <div className="flex flex-col sm:flex-row gap-2 not-prose">
        <button
          type="button"
          onClick={loadSummary}
          disabled={loading}
          className="h-12 px-5 rounded-full bg-white/[0.04] border border-white/15 text-white text-sm font-medium inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-60"
          data-testid="privacy-view-data-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4 text-[#ccff00]" />}
          See my data
        </button>
        <button
          type="button"
          onClick={() => setConfirming(true)}
          disabled={loading}
          className="h-12 px-5 rounded-full bg-red-500/10 border border-red-500/40 text-red-200 text-sm font-medium inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-60"
          data-testid="privacy-delete-data-btn"
        >
          <Trash2 className="w-4 h-4" />
          Delete my data
        </button>
      </div>

      {summary && (
        <div className="mt-4 p-4 rounded-2xl bg-white/[0.03] border border-white/10 text-[13px] text-zinc-300 space-y-1.5" data-testid="privacy-data-summary">
          <div>Device ID: <span className="font-mono text-zinc-400">{summary.device_id_masked}</span></div>
          <div>Parking spots you dropped: {summary.spots}</div>
          <div>Giveaway entry events: {summary.campaign_events}</div>
          <div>
            Giveaway contact stored:{" "}
            {summary.giveaway_email ? "email" : summary.giveaway_instagram ? "Instagram" : "none"}
          </div>
          <div>Invite code: {summary.referral_code || "none"}</div>
          <div>
            Groups:{" "}
            {summary.groups.length === 0
              ? "none"
              : summary.groups
                  .map(
                    (g) =>
                      `${g.name}${g.sharing_live ? " (sharing location)" : ""}${g.sharing_car ? " (car shared)" : ""}`
                  )
                  .join(", ")}
          </div>
        </div>
      )}

      {confirming && (
        <div className="mt-4 p-4 rounded-2xl bg-red-500/[0.07] border border-red-500/30" data-testid="privacy-delete-confirm">
          <p className="text-[13px] text-red-100 leading-relaxed">
            Delete your dropped spots, giveaway entries and registration, invite code, and your
            membership plus any shared locations in every group? This cannot be undone.
          </p>
          <div className="mt-3 flex gap-2 not-prose">
            <button
              type="button"
              onClick={handleDelete}
              disabled={loading}
              className="h-11 px-5 rounded-full bg-red-500 text-white text-sm font-semibold active:scale-[0.98] transition-transform disabled:opacity-60"
              data-testid="privacy-delete-confirm-btn"
            >
              {loading ? "Deleting…" : "Yes, delete"}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="h-11 px-5 rounded-full bg-white/5 border border-white/10 text-zinc-300 text-sm"
              data-testid="privacy-delete-cancel-btn"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {done && (
        <div className="mt-4 p-4 rounded-2xl bg-white/[0.03] border border-white/10" data-testid="privacy-delete-result">
          <div className="text-[13px] text-[#ccff00] font-semibold">Deleted</div>
          <ul className="mt-1.5 text-[13px] text-zinc-400 space-y-0.5 list-none pl-0">
            {Object.entries(done.deleted).map(([k, v]) => (
              <li key={k}>
                {k.replace(/_/g, " ")}: {v}
              </li>
            ))}
          </ul>
          <div className="text-[13px] text-white font-semibold mt-3">
            Kept for safety or because it belongs to someone else
          </div>
          <ul className="mt-1.5 text-[13px] text-zinc-400 space-y-1 list-none pl-0">
            {done.retained.map((r) => (
              <li key={r.data}>
                <span className="text-zinc-300">{r.data}</span> — {r.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      <Toaster theme="dark" position="top-center" />
    </div>
  );
}
